import { IDatetime } from '@framework/date-helpers/Datetime';
import ISector from '@/creditflowresearch/domain/vo/IDealSector';
import ISubsector from '@/creditflowresearch/domain/vo/ISubsector';
import IIndustry from '@/creditflowresearch/domain/vo/IIndustry';
import ISubindustry from '@/creditflowresearch/domain/vo/ISubindustry';
import ITranch, { ITrancheSize } from '@/creditflowresearch/domain/vo/Tranch/ITranch';
import { ISubscriptionStatus } from '@/creditflowresearch/domain/vo/ISubscriptionStatus';

export interface IProduct {
  id: string,
  name: string,
  displayName: string,
  marketDataName: string,
  abbreviation: string,
  mobileAbbreviation: string,
  beta: boolean,
}

export interface ICountry {
  id: string,
  name: string,
  region?: IRegion,
  product?: IProduct,
}

export interface IRegion {
  id: string,
  name: string,
  displayName: string,
  abbreviation: string,
  priority: number,
  shortDisplayName: string,
  timeZoneId: string,
  beta?: boolean,
  countryList: ICountry[],
}

export interface IPrimaryIssuer {
  viewId: string | number,
  id: string,
  name: string,
  countryId?: string,
  industryId?: string,
  subindustryId?: string,
  industry?: IIndustry | null,
  subindustry?: ISubindustry | null,
  publicExchange?: string,
  publicTicker?: string,
  abbreviation?: string,
  issuerTicker: string,
  nickname?: string | null,
  holdco?: boolean | null,
  opco?: boolean | null,
}

export type { IIndustry };
export type { ISubindustry };

export interface IParent {
  viewId: string | number,
  id: string,
  name: string,
  industry: IIndustry,
  subindustry: ISubindustry,
  countryId: string | null,
  countDeals: number,
  publicExchange?: string,
  publicTicker?: string,
  abbreviation?: string,
  issuerTicker: string,
  nickname?: string | null,
  holdco?: boolean | null,
  opco?: boolean | null,
}

export interface IDealSize {
  [key: string]: ITrancheSize,
}

export interface ILead {
  viewId: number,
  id: string,
  name: string,
  industryId: string,
  subindustryId: string,
  publicExchange: string,
  publicTicker: string,
  abbreviation: string,
  issuerTicker: string,
  nickname?: string | null,
  opco?: boolean | null,
  url: string,
}

export interface IJointLead {
  viewId: number,
  id: string,
  name: string,
  industryId: string,
  subindustryId: string,
  publicExchange: string,
  publicTicker: string,
  abbreviation: string,
  issuerTicker: string,
}

export enum DealType {
  SECTOR = 'sector',
  INDUSTRY = 'industry',
}

export enum DealStatus {
  Pulled = 'pulled',
  InMarket = 'inMarket',
  Priced = 'priced',
}

export interface IDealFiling {
  acceptanceDate: IDatetime,
  type: string,
  url: string,
}

export default interface IDeal {
  status: IDealStatus,
  announcedDate?: null | IDatetime,
  collateral?: string | null,
  comments: string,
  country: ICountry,
  dealType: DealType,
  guarantor?: string | null,
  id: string,
  investorSetAvailable: boolean,
  jointLeads: IJointLead[],
  originator?: string | null,
  parent: IParent,
  pricingDate: IDatetime,
  primaryIssuer: IPrimaryIssuer,
  product: IProduct,
  regionId?: string,
  riskRetention?: string | null,
  sector: ISector,
  seriesName: string,
  structuringLeads: ILead[],
  subsector: ISubsector,
  totalSize: IDealSize,
  tranches: ITranch[],
  useOfProceeds?: string | null,
  verified: boolean,
  viewId: number,
  volckerCompliance?: string | null,
  filings?: IDealFiling[] | null,
  relativeValue?: string | null,
}

export interface IDealsPayload {
  page: number,
  perPage?: number,
  filters: {
    hasEitherPricingOrAnnouncedDate?: boolean,
    includedDealViewIds: string[] | number[],
    includedProductIds: string[],
    includedRegionIds: string[],
    excludedSectorIds?: string[],
    includedSectorId?: string,
    includedSubsectorId?: string,
    includedSubindustryId?: string,
    includedIndustryIds?: string[],
    includedSubindustryIds?: string[],
    includedStatusGroups?: DealStatus[],
    dateFrom?: string | null,
    dateTo?: string | null,
  },
  filterType: string,
}

export interface IDealsWithSubscriptionPayload {
  filterType: string,
  filters: {
    includedCompanyId?: string,
    includedCompanyRole?: string[],
    includedDealViewIds?: string[],
    includedStatusGroups?: DealStatus[],
    excludedSectorIds?: string[],
    includedProductIds: string[],
    includedRegionIds: string[],
    includedIndustryId?: string,
    includedSubindustryId?: string,
    hasEitherPricingOrAnnouncedDate: boolean,
    query?: string[],
  },
  page: number,
  perPage: number,
  sortBy?: string,
  sortOrder?: string,
}

export interface IDealStatus {
  name: string,
  abbreviation: string,
}

export interface IIssuer {
  abbreviation: string,
  issuerTicker: string,
  label: string,
  name: string,
  value: string,
}

export interface IDealResponse {
  collection: IDeal[],
  totalCount: number,
  obfuscated?: boolean,
  hasServerError?: boolean,
  pageFilters?: IDealsPayload,
  subscriptionStatus?: ISubscriptionStatus,
}
